/**
 * Banking module type definitions
 * Used across Banking Dashboard and related pages
 */

import type { ConnectionAttention } from '@/lib/ledgersync-connection-attention'

/**
 * Transaction sources that are user-authored (manual entries + file imports),
 * as opposed to read-only bank-feed rows (Plaid / LedgerSync / Sage). Single
 * source of truth for "which rows the user may read on the manual-transactions
 * tab and write to": the data layer (`db-tenant`) scopes manual-transaction
 * reads/writes to these, and the LOC drill-down dialog gates inline Notes
 * editing on them. Lives in this client-free types module so the presentational
 * dialog can read it WITHOUT pulling the Supabase client into its bundle (DCP-169).
 */
export const MANUAL_PAGE_TRANSACTION_SOURCES = ['manual', 'file_import'] as const

/**
 * Transaction sources that arrive through a bank-feed API rather than a human
 * (LedgerSync today; `plaid` is the abandoned predecessor whose historic rows
 * are still bank-feed rows). Counterpart of {@link MANUAL_PAGE_TRANSACTION_SOURCES}
 * and the discriminator behind the Dashboard's "API Status" column (DCP-344).
 */
export const API_TRANSACTION_SOURCES = ['ledgersync', 'plaid'] as const

/**
 * Whether a transaction reached us through a bank-feed API.
 *
 * Gated on `transactions.source` — NOT on `transactions.sync_status`. `source`
 * is `NOT NULL` and has been written explicitly by every insert path since the
 * table was created, so it classifies historic rows too; `sync_status` only
 * ever says "the ingest mapper set it to synced", which no manual or
 * file-import row ever does (DCP-344).
 *
 * @param source - Raw `transactions.source` value; null/undefined is treated as
 *                 not-API (the column is NOT NULL, so this is defensive only).
 * @returns true for bank-feed rows, false for manual entries and file imports.
 */
export function isApiTransactionSource(source: string | null | undefined): boolean {
  return !!source && (API_TRANSACTION_SOURCES as readonly string[]).includes(source)
}

/**
 * KPI data for banking dashboard summary cards.
 *
 * `intacctBalance` and `bookVsIntacct` are `null` when the Intacct sum could not
 * be loaded: a failed read must render as "unknown", not as a $0 balance that
 * would also turn the variance into the whole Book Balance.
 */
export interface BankingKpi {
  bankBalance: number
  outstandingChecks: number
  bookBalance: number
  intacctBalance: number | null
  /** Book Balance − Intacct Balance, rounded to cents (`book_vs_intacct` in CONTEXT.md). */
  bookVsIntacct: number | null
}

/**
 * Per-tile "data missing for the current day" warnings (DCP-218 AC.3).
 * Keyed by the same metric keys as {@link BankingKpi}; the value is a
 * human-readable explanation of what is missing/stale, shown via a warning
 * icon + tooltip on the corresponding KPI tile. A key is absent when the
 * tile's data is complete for the current day.
 */
export type BankingKpiWarnings = Partial<Record<keyof BankingKpi, string>>

/**
 * The full result of {@link useBankingKpi}: the aggregated numbers plus the
 * per-tile freshness warnings computed for AC.3.
 */
export interface BankingKpiResult extends BankingKpi {
  warnings: BankingKpiWarnings
}

/**
 * Known Plaid personal_finance_category.primary values.
 * Uses a union with `string` to allow unknown categories from the API
 * while still providing autocomplete for known values.
 */
export type PlaidCategory =
  | 'INCOME'
  | 'FOOD_AND_DRINK'
  | 'TRANSPORTATION'
  | 'ENTERTAINMENT'
  | 'GENERAL_MERCHANDISE'
  | 'RENT_AND_UTILITIES'
  | 'TRANSFER_OUT'
  | 'TRANSFER_IN'
  | 'LOAN_PAYMENTS'
  | 'BANK_FEES'
  | 'MEDICAL'
  | 'PERSONAL_CARE'
  | 'TRAVEL'
  | 'OTHER'
  // eslint-disable-next-line @typescript-eslint/ban-types
  | (string & {})

/**
 * Bank transaction record
 */
export interface BankTransaction {
  id: string
  facility: string
  date: string
  accountNumber: string
  accountName: string
  category: PlaidCategory
  /**
   * `transactions.category_id`, or `null` for an uncategorized row (DCP-123).
   * Drives the inline Category dropdown and the yellow "needs a category"
   * highlight; `category` above stays the display label ("No Category" when
   * this is null).
   */
  categoryId: string | null
  /**
   * Matched category type — drives the displayed sign/colour of `amount`
   * via formatCategoryAmount (inflow → +green, outflow → −red). Null or
   * absent → fall back to the stored sign of `amount`.
   */
  categoryType?: 'inflow' | 'outflow' | null
  amount: number
  description: string
  memo: string
  /**
   * True while the bank still reports the charge as pending (DCP-355).
   *
   * Distinct from `apiStatus`, which is about OUR ingest: a row can be
   * `apiStatus: 'synced'` (we pulled it fine) and `pending: true` (the bank has
   * not settled it). Only bank-feed sources can be pending — manual entries and
   * file imports are always false.
   */
  pending: boolean
  /**
   * Bank-feed ingest status, rendered by the Dashboard's "API Status" column.
   * `null` means "not applicable" — the row was entered manually or imported
   * from a file, so there is no API to report on and the cell shows "-"
   * (DCP-344).
   */
  apiStatus: ApiSyncStatus | null
}

/**
 * Bank-feed ingest status of a single transaction.
 *
 * Mirrors the `public.sync_status` enum on `transactions.sync_status`, which
 * the LedgerSync ingest mapper sets to `'synced'` on arrival. It is NOT a Sage
 * Intacct status: sending transactions to Sage does not exist (DCP-194 is in
 * Backlog, marked "they didn't want to receive anything in Sage"), which is why
 * the column was renamed away from "Record in Sage status" in DCP-344.
 */
export type ApiSyncStatus = 'synced' | 'pending' | 'error'

/**
 * Data for facility-category horizontal bar chart.
 * Keys are the facility name plus dynamic category names from the user-defined
 * `categories` table (DCP-223), each mapping to the numeric transaction total.
 */
export interface FacilityCategoryData {
  facility: string
  [category: string]: string | number
}

/**
 * Signed net totals for the facility/category chart (DCP-223 part 2), keyed
 * `facilityName → categoryName → signed net`. Kept SEPARATE from the chart
 * `rows` (which carry only the plotted magnitude `ABS(net)` per category) so a
 * user-defined category name can never collide with a reserved sentinel key.
 * The tooltip looks the signed value up here so the displayed sign comes from
 * the arithmetic net, not the (deprecated) category type.
 */
export type FacilityCategorySignedTotals = Record<string, Record<string, number>>

/**
 * Account import status for Transactions Import page
 */
export interface AccountImportStatus {
  id: string
  facility: string
  bank: string
  accountName: string
  accountNumber: string
  numberOfRows: number | null
  apiUpdateStatus: ImportStatus
  apiUpdateMessage?: string
  lastApiUpdateTimestamp: string
  lastUpdateSource: UpdateSource
  lastUpdateTimestamp: string
}

/**
 * Import status values
 */
export type ImportStatus = 'success' | 'failed' | 'pending'

/**
 * Source of last update
 */
export type UpdateSource = 'API' | 'File Import' | 'Manual'

/**
 * Pack — a named grouping of facilities (DCP-171).
 *
 * A pack aggregates several facilities so the platform can report a Cash
 * Position Summary per pack. The facility ↔ pack link lives on
 * `facilities.pack_id` (one-to-many): a facility belongs to at most one pack,
 * which is what structurally guarantees AC #4 ("a facility cannot be added to
 * multiple packs").
 */
export interface Pack {
  id: string
  name: string
}

/**
 * Payload for creating a pack from the settings UI.
 *
 * @property name - Mandatory pack name (AC #3).
 * @property facility_ids - Facilities to assign to the new pack (AC #1, #3).
 *   Assigned by stamping `pack_id` on each selected facility.
 */
export interface PackInput {
  name: string
  facility_ids: string[]
}

/**
 * Payload for updating a pack.
 *
 * @property facility_ids - The full desired membership; the update reconciles
 *   facilities to this set (un-assigns dropped ones, assigns added ones).
 */
export interface PackUpdate {
  name?: string
  facility_ids?: string[]
}

/**
 * Origin of a facility row.
 *   - 'manual'       — created by an admin via the Add Facility dialog.
 *   - 'sage_intacct' — auto-imported from Sage Intacct locations (DCP-262).
 * Mirrors the `facilities.source` DB column (default 'manual').
 */
export type FacilitySource = 'manual' | 'sage_intacct'

/**
 * Facility information
 *
 * @property pack_id - Id of the {@link Pack} this facility belongs to, or
 *   `null` when it is not assigned to any pack. Managed from the Packs
 *   settings page, not the facility form.
 * @property source - Where the facility came from — manual entry vs. an
 *   auto-import from Sage Intacct (DCP-262). Drives the Source column on the
 *   Settings → Facilities list.
 * @property name - The Display Name: the short name every screen shows.
 * @property legal_name - The Legal Name ([ADR 0021](../../../../docs/adr/0021-facility-display-and-legal-names.md));
 *   `null` until the Sage sync fills it or a user enters it.
 */
export interface Facility {
  id: string
  name: string
  legal_name: string | null
  code: string
  sage_location_id: string | null
  is_active: boolean
  pack_id: string | null
  source: FacilitySource
}

/**
 * Bank account row as returned from the tenant database.
 *
 * DCP-324: `status` is deliberately absent. It mirrored
 * `bank_accounts.status`, which migration 20260910050000 drops — the column was
 * NOT NULL DEFAULT 'active' and the product never wrote any other value, so it
 * carried no information. Nothing rendered or branched on it, which is why its
 * removal needs no replacement field here. Whether a CONNECTED account is live
 * is now derived per account from `sync_disabled_at` / `provider_removed_at`
 * (see `get_connection_accounts`) and is surfaced by the connection-management
 * read model, not by this row shape.
 */
export interface BankAccountRow {
  id: string
  name: string
  institution_name: string | null
  account_number: string | null
  facility_id: string | null
  account_type: string | null
  integration_id: string | null
  /** Counts towards Bank Balance and the Cash Position Summary; opt-in, false by default. */
  include_in_cash_position: boolean
}

/** Payload for creating a bank account */
export interface BankAccountInput {
  name: string
  institution_name: string
  account_type: string
  account_number?: string | null
  facility_id?: string | null
}

/** Payload for updating a bank account */
export interface BankAccountUpdate {
  name?: string
  institution_name?: string
  account_type?: string
  account_number?: string | null
  facility_id?: string | null
}

/**
 * Payload for updating a facility from the settings UI.
 * All fields are optional — only provided fields are persisted.
 *
 * Pack membership (`pack_id`) is intentionally NOT part of this payload: it is
 * managed exclusively from the Packs settings page via the pack hooks, so the
 * facility form can't set or clear it (and can't bypass the AC #4 conflict
 * check in `assignToPack`).
 *
 * @property sage_location_id - Sage Intacct location ID. The DB column is
 *   `TEXT UNIQUE` (nullable) — see `00000000000000_schema.sql`. The settings
 *   UI (Add / Edit facility dialogs introduced in DCP-154) requires a
 *   non-empty value before submit, which is a deliberate product asymmetry:
 *   we want every NEW or EDITED facility to carry the Sage mapping so the
 *   Checks / Bank Accounts sync can resolve it. Legacy rows with NULL
 *   continue to exist and read fine; the moment they're opened in the Edit
 *   dialog the user is required to fill the field before saving (this is
 *   surfaced via the Zod schema on the form, not via the column type).
 */
export interface FacilityUpdate {
  name?: string
  legal_name?: string
  sage_location_id?: string
  code?: string | null
  is_active?: boolean
}

/**
 * Payload for creating a facility from the settings UI.
 *
 * `name` and `sage_location_id` are required to mirror the form's submit
 * gate (Sage mapping is mandatory on create per the comment on
 * `FacilityUpdate.sage_location_id`); the other fields default to
 * sensible values in the dialog. Optional fields keep the same shape as
 * `FacilityUpdate` so the two payloads can share validation / formatting
 * helpers (see `normalizeOptionalFacilityField`).
 */
export interface FacilityInput {
  name: string
  legal_name: string
  sage_location_id: string
  code?: string | null
  is_active?: boolean
}

/**
 * Filters for banking data queries
 *
 * @property startDate - Optional start date filter (format: YYYY-MM-DD)
 * @property endDate - Optional end date filter (format: YYYY-MM-DD)
 * @property facilityIds - Optional filter by multiple facilities/locations
 * @property accountIds - Optional filter by multiple bank accounts
 * @property categories - Optional filter by multiple transaction categories
 */
export interface BankingFilters {
  startDate?: string
  endDate?: string
  facilityIds?: string[]
  accountIds?: string[]
  categories?: string[]
}

/**
 * Category-filter value that selects UNCATEGORIZED rows (`category_id IS NULL`),
 * offered next to the real categories in the Daily Banking Category filter
 * (DCP-123, AC#5). Lowercase on purpose: every other filter value is either a
 * UUID or an upper-case legacy Plaid key (`PLAID_CATEGORY_REGEX`), so this one
 * can never collide with — or be mistaken for — either of them.
 */
export const NO_CATEGORY_FILTER_VALUE = 'none'

/** Label shown for an uncategorized row and for {@link NO_CATEGORY_FILTER_VALUE}. */
export const NO_CATEGORY_LABEL = 'No Category'

/**
 * Columns of the Daily Banking table that can be sorted server-side (DCP-123,
 * AC#6) — every data column but MEMO. The values are the table's column KEYS
 * (`sageStatus` is the "API Status" column — see its note in
 * BankTransactionsTable), so a DataTable sort event maps onto this type without
 * a translation table. Single source for the type, the URL parser and the
 * table's sort-event guard.
 */
export const BANK_TRANSACTION_SORT_KEYS = [
  'facility',
  'date',
  'accountNumber',
  'accountName',
  'category',
  'amount',
  'description',
  'sageStatus',
] as const

/** A sortable Daily Banking column key — see {@link BANK_TRANSACTION_SORT_KEYS}. */
export type BankTransactionSortKey = (typeof BANK_TRANSACTION_SORT_KEYS)[number]

/**
 * Narrow an untrusted value (a URL param, a DataTable `columnKey`) to a
 * {@link BankTransactionSortKey}. An array lookup, not an object index, so a
 * hand-edited `?sort=constructor` can never resolve to a prototype member.
 *
 * @param value - Candidate key; `null` (absent URL param) is never a key.
 * @returns Whether `value` names a sortable column.
 */
export function isBankTransactionSortKey(value: string | null): value is BankTransactionSortKey {
  return value !== null && (BANK_TRANSACTION_SORT_KEYS as readonly string[]).includes(value)
}

/** Active sort of the Daily Banking table; absent = default (newest first). */
export interface BankTransactionSort {
  key: BankTransactionSortKey
  direction: 'asc' | 'desc'
}

/**
 * A category the user can assign to a transaction from the Daily Banking table
 * (inline dropdown and bulk action).
 */
export interface AssignableCategory {
  id: string
  name: string
  type: 'inflow' | 'outflow' | null
}

/**
 * Payload for creating/updating a manual transaction.
 *
 * DCP-324: there is deliberately NO `facility_id`. A transaction is not linked
 * to a facility — `bank_account_id` carries it, because the account is what the
 * facility is assigned to. The old column was a copy stamped at INSERT that no
 * reassignment ever refreshed, so a row kept pointing at the facility the
 * account had on the day it was written. The Add/Edit dialogs still ASK for a
 * facility: it is how the user narrows the account list, and the submit gate
 * checks the chosen account really belongs to it — but the answer is not
 * persisted, it is re-derived on every read.
 */
export interface ManualTransactionInput {
  bank_account_id: string | null
  category_id: string | null
  transaction_date: string
  amount: number
  description: string
  user_id?: string | null
}

/**
 * Filters for manual transactions queries
 */
export interface ManualTransactionFilters {
  startDate?: string
  endDate?: string
  facilityIds?: string[]
  limit?: number
  offset?: number
}

/**
 * Which reference a LOC Usage drill-down is scoped to (DCP-333).
 *
 * The drill-down used to load every LOC transaction in the organization at once
 * and slice it client-side; PostgREST's `max_rows = 1000` truncated that feed
 * silently, so lines past the cut opened an empty pop-up. Each pop-up now asks
 * only for its own rows, and this discriminated union names the column the
 * request filters on — see {@link useLocTransactions} in `@/hooks/useBanking`.
 *
 * Declared here rather than next to the hook because `@/lib/query-keys` needs it
 * for the scoped cache key and must stay free of runtime dependencies (importing
 * it from the hook module would point a key module back at the hooks graph).
 */
export type LocTxnScope =
  /** A pack's child line row: one `loc_lines` row. */
  | { kind: 'line'; id: string }
  /** A standalone facility row, or a Cash Position facility column. */
  | { kind: 'facility'; id: string }
  /** A pack parent row, or a Cash Position pack-subtotal column. */
  | { kind: 'pack'; id: string }

/**
 * Tab options for Transactions Import page.
 * DCP-269: only the two implemented tabs remain; the empty "coming soon" tabs
 * (account import status, categorization, API import, import history) were removed.
 */
export type TransactionsImportTab =
  | 'transactions-files-upload'
  | 'transactions-manual-input'

/**
 * KPI data for cross-checking page summary cards
 */
export interface CrossCheckingKpi {
  bankBalance: number
  outstandingChecks: number
  bookBalance: number
  intacctBalance: number
  bookVsIntacct: number
}

/**
 * Extended transaction record for cross-checking with additional fields
 */
export interface CrossCheckingTransaction extends BankTransaction {
  bank: string
  transactionNumber: string
  transactionStatus: TransactionStatus
  paymentInfo: string
  notes?: string
  recordInSageStatus?: 'synced' | 'pending' | 'failed'
}

/**
 * Transaction status for cross-checking
 */
export type TransactionStatus = 'DEPOSIT' | 'LINE OF CREDIT' | 'ACCTS PAYABLE' | 'PAYROLL'

/**
 * Tab options for Cross-checking page
 */
export type CrossCheckingTab = 'matching' | 'intact-balance-report' | 'history'

/**
 * Tab options for Balance Import page.
 * DCP-267: only the two implemented tabs remain; the empty "balance update
 * status" and "balance import history" placeholder tabs were hidden.
 */
export type BalanceImportTab =
  | 'balance-manual-update'
  | 'api-balance-import'

/**
 * Balance import status record for Balance Import page.
 *
 * One row per bank account; manual accounts (no integration) are editable
 * inline, API-backed accounts (Plaid / Sage / other) are read-only.
 *
 * @property bankAccountId - UUID of the underlying `bank_accounts` row. Used
 *   by the inline-edit mutation to target the right account. Stored
 *   alongside `id` (which keeps DataTable `rowKey` stable across refetches)
 *   so callers don't have to reverse-map the rendered key back to the FK.
 * @property isApiAccount - True when the bank account has an `integration_id`
 *   in the tenant DB. The inline edit affordance is hidden for these rows —
 *   their balance is owned by the upstream API and any manual write would be
 *   overwritten on the next sync.
 */
export interface BalanceImportStatus {
  id: string
  bankAccountId: string
  isApiAccount: boolean
  /**
   * DCP-338: set ONLY when the account's LedgerSync connection stopped
   * delivering — `failed` (credentials/consent died at the bank),
   * `disconnected` (the user revoked access upstream), `requires_action` (the
   * bank wants the user to sign in or confirm access again), or `unknown` for a
   * status newer than this bundle — see `connectionAttention`. Undefined for
   * manual accounts and for healthy connections.
   *
   * A broken connection keeps its account listed here and keeps its last known
   * balance counting in Bank Balance / Cash Position Summary (product decision
   * on DCP-338, 2026-08-18). This flag is what stops that from being silent:
   * the table
   * renders a "connection lost" marker so the stale figure is visibly stale and
   * the user knows the bank needs reconnecting.
   */
  connectionStatus?: ConnectionAttention
  facility: string
  bank: string
  accountName: string
  accountNumber: string
  bankBalance: number
  apiUpdateStatus: ImportStatus
  apiUpdateMessage?: string
  lastApiUpdateTimestamp: string
  lastUpdateSource: UpdateSource
  lastUpdateTimestamp: string
}

/** Status of a file import job */
export type FileImportStatus = 'pending' | 'processing' | 'completed' | 'failed'

/** Discriminator for file import targets — transactions or projections */
export type ImportType = 'transaction' | 'projection'

/** All valid ImportType values — single source of truth for runtime validation */
const IMPORT_TYPES: readonly ImportType[] = ['transaction', 'projection'] as const

/**
 * Type guard that narrows an unknown string to ImportType.
 * Preferred over `(array as readonly string[]).includes(...)` casts.
 * @param value - Value to check
 */
export function isValidImportType(value: string): value is ImportType {
  return (IMPORT_TYPES as readonly string[]).includes(value)
}

/** Row from the file_imports table */
export interface FileImportRow {
  id: string
  user_id: string | null
  file_name: string
  file_path: string
  file_type: 'csv' | 'xlsx'
  status: FileImportStatus
  total_rows: number
  imported_rows: number
  failed_rows: number
  errors: Array<{ row: number; message: string }>
  created_at: string
  updated_at: string
  import_type: ImportType
  /** Joined from profiles table */
  profiles?: { full_name: string }
}

/** Input payload for creating a file import record */
export interface FileImportInput {
  file_name: string
  file_path: string
  file_type: 'csv' | 'xlsx'
  user_id: string
  import_type: ImportType
}